import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, MapPin, Clock } from 'lucide-react';
import { allEvents, sortEvents } from '../utils/events';
import EventCard from '../components/EventCard';

const Events: React.FC = () => {
  const { t } = useTranslation();
  const [showAllPastEvents, setShowAllPastEvents] = useState(false);

  // Sort events into upcoming and past based on current date
  const { upcomingEvents, pastEvents } = useMemo(() => {
    return sortEvents(allEvents);
  }, []);

  // Show only first 5 past events initially
  const displayedPastEvents = showAllPastEvents ? pastEvents : pastEvents.slice(0, 5);

  return (
    <section className="page-section">
      <div className="page-container">
        <div className="page-intro">
          <p className="eyebrow">Community calendar</p>
          <h1 className="page-title">{t('events.title', 'Events')}</h1>
          <p className="page-description">Gather with the JAJ community for cultural celebrations, workshops, and local events.</p>
        </div>

        {/* Upcoming Events Section */}
        {upcomingEvents.length > 0 && (
          <div className="mb-14">
            <h2 className="section-title mb-6">
              {t('events.calendar.title', 'Upcoming Events')}
            </h2>
            <div className="grid gap-5 lg:grid-cols-2">
              {upcomingEvents.map(event => (
                <EventCard key={event.id} event={event} isUpcoming={true} />
              ))}
            </div>
          </div>
        )}

        {/* Past Events Section */}
        {pastEvents.length > 0 && (
          <div className="mb-14">
            <h2 className="section-title mb-6">Past Events</h2>
            <div className="grid gap-5 lg:grid-cols-2">
              {displayedPastEvents.map(event => (
                <EventCard key={event.id} event={event} isUpcoming={false} />
              ))}
            </div>
            {pastEvents.length > 5 && (
              <div className="mt-6 text-center">
                <button
                  onClick={() => setShowAllPastEvents(!showAllPastEvents)}
                  className="btn-secondary"
                >
                  {showAllPastEvents ? 'Show Less' : `Show More (${pastEvents.length - 5} more)`}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Garage Sale Event */}
        <div className="mt-12">
          <div className="overflow-hidden bg-[#8b5d12] p-6 text-white shadow-[0_16px_45px_rgba(80,55,15,0.18)] sm:p-8">
            <div className="mb-6">
              <h2 className="text-3xl font-bold mb-4">{t('events.garageTitle')}</h2>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Calendar className="w-5 h-5" />
                  <span className="font-semibold">{t('events.garageDate')}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Clock className="w-5 h-5" />
                  <span className="font-semibold">{t('events.garageTime')}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <MapPin className="w-5 h-5" />
                  <span className="font-semibold">{t('events.garageAddress')}</span>
                </div>
              </div>
            </div>
            <div className="space-y-4 text-sm">
              <p>{t('events.garageDonation')}</p>
              <p className="font-semibold">{t('events.garageDropOff')}</p>
              <p>{t('events.garageDropOffAddress')}</p>
              <p className="text-xs bg-white/20 p-3 rounded-lg">
                {t('events.garageJapanese')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Events; 
